import {
    BufferGeometry,
    Camera,
    Mesh,
    Object3D,
    OrthographicCamera,
    Vector3,
    Vector4,
} from "three";
import ClipperLib from "clipper-lib";

export interface SilhouetteParams {
    strokeWidth: number;
    strokeColor: string;
    includeCreases: boolean;
    creaseAngleDeg: number;
    outputWidth: number;
    outputHeight: number;
    unionPaths: boolean;
}

const CLIPPER_SCALE = 100_000;

function toClipperPt(x: number, y: number): { X: number; Y: number } {
    return new ClipperLib.IntPoint2(
        Math.round(x * CLIPPER_SCALE),
        Math.round(y * CLIPPER_SCALE),
    );
}

function clipperPathToD(path: { X: number; Y: number }[]): string {
    if (path.length < 2) return "";
    const fmt = (n: number) => String(Math.round((n / CLIPPER_SCALE) * 100) / 100);
    let d = `M${fmt(path[0].X)} ${fmt(path[0].Y)}`;
    for (let i = 1; i < path.length; i++) d += `L${fmt(path[i].X)} ${fmt(path[i].Y)}`;
    return d + "Z";
}

function buildUnionSvg(paths: SvgPt[][], params: SilhouetteParams): string {
    const { strokeColor: color, outputWidth: w, outputHeight: h } = params;
    const empty = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"></svg>`;

    // Need ≥ 3 points to form a polygon; close open polylines
    const clipperPaths = paths
        .filter((p) => p.length >= 3)
        .map((p) => p.map((pt) => toClipperPt(pt.x, pt.y)));

    if (clipperPaths.length === 0) return empty;

    const clipper = new ClipperLib.Clipper();
    clipper.AddPaths(clipperPaths, ClipperLib.PolyType.ptSubject, true);
    const solution = new ClipperLib.Paths();
    clipper.Execute(
        ClipperLib.ClipType.ctUnion,
        solution,
        ClipperLib.PolyFillType.pftNonZero,
        ClipperLib.PolyFillType.pftNonZero,
    );

    if (!solution || solution.length === 0) return empty;

    const d = (solution as { X: number; Y: number }[][])
        .map(clipperPathToD)
        .filter(Boolean)
        .join("");

    if (!d) return empty;

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <path d="${d}" fill="${color}" fill-rule="evenodd"/>
</svg>`;
}

interface EdgeInfo {
    va: Vector3;
    vb: Vector3;
    facings: number[];
    normals: Vector3[];
}

function vertKey(v: Vector3): string {
    return `${Math.round(v.x * 1e4)},${Math.round(v.y * 1e4)},${Math.round(v.z * 1e4)}`;
}

function edgeKey(va: Vector3, vb: Vector3): string {
    const ka = vertKey(va);
    const kb = vertKey(vb);
    return ka <= kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}

function r2(n: number): number {
    return Math.round(n * 100) / 100;
}

function projectWorldToSvg(
    worldPos: Vector3,
    camera: Camera,
    w: number,
    h: number,
): { x: number; y: number } | null {
    const p = new Vector4(worldPos.x, worldPos.y, worldPos.z, 1.0);
    p.applyMatrix4(camera.matrixWorldInverse);
    p.applyMatrix4(camera.projectionMatrix);
    if (p.w <= 0) return null;
    const nx = p.x / p.w;
    const ny = p.y / p.w;
    return {
        x: (nx + 1) * 0.5 * w,
        y: (1 - (ny + 1) * 0.5) * h,
    };
}

interface SvgPt { x: number; y: number }

function ptKey(x: number, y: number): string {
    return `${Math.round(x * 100)},${Math.round(y * 100)}`;
}

function chainEdgesToPaths(
    edges: Array<{ ax: number; ay: number; bx: number; by: number }>,
): SvgPt[][] {
    if (edges.length === 0) return [];

    type AdjEntry = { edgeIdx: number; otherKey: string; otherPt: SvgPt };
    const adj = new Map<string, AdjEntry[]>();

    for (let i = 0; i < edges.length; i++) {
        const { ax, ay, bx, by } = edges[i];
        const ka = ptKey(ax, ay);
        const kb = ptKey(bx, by);
        if (!adj.has(ka)) adj.set(ka, []);
        if (!adj.has(kb)) adj.set(kb, []);
        adj.get(ka)!.push({ edgeIdx: i, otherKey: kb, otherPt: { x: bx, y: by } });
        adj.get(kb)!.push({ edgeIdx: i, otherKey: ka, otherPt: { x: ax, y: ay } });
    }

    const usedEdges = new Set<number>();
    const paths: SvgPt[][] = [];

    for (let startIdx = 0; startIdx < edges.length; startIdx++) {
        if (usedEdges.has(startIdx)) continue;
        usedEdges.add(startIdx);

        const { ax, ay, bx, by } = edges[startIdx];
        const path: SvgPt[] = [{ x: ax, y: ay }, { x: bx, y: by }];

        // Extend forward from end
        let extended = true;
        while (extended) {
            extended = false;
            const last = path[path.length - 1];
            for (const { edgeIdx, otherPt } of adj.get(ptKey(last.x, last.y)) ?? []) {
                if (usedEdges.has(edgeIdx)) continue;
                usedEdges.add(edgeIdx);
                path.push(otherPt);
                extended = true;
                break;
            }
        }

        // Extend backward from start
        extended = true;
        while (extended) {
            extended = false;
            const first = path[0];
            for (const { edgeIdx, otherPt } of adj.get(ptKey(first.x, first.y)) ?? []) {
                if (usedEdges.has(edgeIdx)) continue;
                usedEdges.add(edgeIdx);
                path.unshift(otherPt);
                extended = true;
                break;
            }
        }

        if (path.length >= 2) paths.push(path);
    }

    return paths;
}

export function extractSilhouette(
    model: Object3D,
    camera: Camera,
    params: SilhouetteParams,
): string {
    model.updateMatrixWorld(true);

    const isOrtho = camera instanceof OrthographicCamera;
    const cameraPos = new Vector3();
    camera.getWorldPosition(cameraPos);
    const camForward = new Vector3();
    camera.getWorldDirection(camForward);

    const edgeMap = new Map<string, EdgeInfo>();

    model.traverse((child) => {
        if (!(child instanceof Mesh)) return;

        const rawGeo = child.geometry as BufferGeometry;
        // toNonIndexed() returns `this` when already non-indexed (common in OBJ/FBX),
        // which would mutate the shared geometry. Always work on a detached copy.
        const geo = rawGeo.index !== null ? rawGeo.toNonIndexed() : rawGeo.clone();
        geo.applyMatrix4(child.matrixWorld);

        const posAttr = geo.getAttribute("position");
        if (!posAttr) return;

        const count = posAttr.count;
        const v0 = new Vector3();
        const v1 = new Vector3();
        const v2 = new Vector3();
        const e1 = new Vector3();
        const e2 = new Vector3();

        for (let i = 0; i < count; i += 3) {
            v0.fromBufferAttribute(posAttr, i);
            v1.fromBufferAttribute(posAttr, i + 1);
            v2.fromBufferAttribute(posAttr, i + 2);

            e1.subVectors(v1, v0);
            e2.subVectors(v2, v0);
            const normal = new Vector3().crossVectors(e1, e2);

            if (normal.lengthSq() < 1e-10) continue;
            normal.normalize();

            let facing: number;
            if (isOrtho) {
                facing = -normal.dot(camForward);
            } else {
                const center = new Vector3()
                    .addVectors(v0, v1)
                    .add(v2)
                    .divideScalar(3);
                facing = normal.dot(new Vector3().subVectors(cameraPos, center));
            }

            const verts = [v0, v1, v2] as const;
            for (let e = 0; e < 3; e++) {
                const va = verts[e];
                const vb = verts[(e + 1) % 3];
                const key = edgeKey(va, vb);

                const existing = edgeMap.get(key);
                if (existing) {
                    existing.facings.push(facing);
                    existing.normals.push(normal.clone());
                } else {
                    edgeMap.set(key, {
                        va: va.clone(),
                        vb: vb.clone(),
                        facings: [facing],
                        normals: [normal.clone()],
                    });
                }
            }
        }
    });

    if (edgeMap.size === 0) {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${params.outputWidth}" height="${params.outputHeight}" viewBox="0 0 ${params.outputWidth} ${params.outputHeight}"></svg>`;
    }

    const includedKeys = new Set<string>();
    const edgesToDraw: Array<{ a: Vector3; b: Vector3 }> = [];

    for (const [key, info] of edgeMap) {
        let include = false;

        if (info.facings.length === 1) {
            include = true;
        } else if (info.facings.length >= 2) {
            if (info.facings[0] * info.facings[1] <= 0) include = true;

            if (!include && params.includeCreases && info.normals.length >= 2) {
                const dot = Math.max(-1, Math.min(1, info.normals[0].dot(info.normals[1])));
                const angleDeg = Math.acos(dot) * (180 / Math.PI);
                if (angleDeg > params.creaseAngleDeg) include = true;
            }
        }

        if (include && !includedKeys.has(key)) {
            includedKeys.add(key);
            edgesToDraw.push({ a: info.va, b: info.vb });
        }
    }

    if (edgesToDraw.length === 0) {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${params.outputWidth}" height="${params.outputHeight}" viewBox="0 0 ${params.outputWidth} ${params.outputHeight}"></svg>`;
    }

    const svgEdges: Array<{ ax: number; ay: number; bx: number; by: number }> = [];

    for (const { a, b } of edgesToDraw) {
        const pa = projectWorldToSvg(a, camera, params.outputWidth, params.outputHeight);
        const pb = projectWorldToSvg(b, camera, params.outputWidth, params.outputHeight);
        if (!pa || !pb) continue;
        svgEdges.push({ ax: pa.x, ay: pa.y, bx: pb.x, by: pb.y });
    }

    const paths = chainEdgesToPaths(svgEdges);

    if (params.unionPaths) return buildUnionSvg(paths, params);

    const strokeAttr = `fill="none" stroke="${params.strokeColor}" stroke-width="${params.strokeWidth}" stroke-linecap="round" stroke-linejoin="round"`;

    const pathEls = paths
        .map((pts) => {
            const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${r2(p.x)} ${r2(p.y)}`).join(" ");
            return `  <path d="${d}" ${strokeAttr}/>`;
        })
        .join("\n");

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${params.outputWidth}" height="${params.outputHeight}" viewBox="0 0 ${params.outputWidth} ${params.outputHeight}">\n${pathEls}\n</svg>`;
}
